import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Image,
  Text,
  VStack,
  Heading,
  Tab,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  useToast,
  Button,
  HStack,
  Spacer,
  Link,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import {
  ArrowUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import { Link as RouterLink } from "react-router-dom";
import CountryFlag from "react-country-flag";
import countries from "i18n-iso-countries";
import eng from "i18n-iso-countries/langs/en.json";

import axiosInstance from "../../helpers/axiosInstance"; // Ensure axiosInstance is correctly imported
import { formatDate } from "../../helpers/utils";

import HotelsCard from "../chat/HotelsCard";
import Activities from "../chat/Activities"; // Ensure the correct import
import FlightCard from "../chat/FlightCard";
import RVRentalCard from "../chat/RVRentalCard";
import InfoCard from "./InfoCard";
import { useCurrency } from "../CurrencyExchange/CurrencyContext"; // Import useCurrency hook
import { ReactComponent as DonateWhiteIcon } from "../../assets/icons/donate-white.svg";
import Translate from "../../components/LanguageTranslation/Translate";
import { Helmet } from "react-helmet-async";
import CategoryFilterMenu from "../BucketList/CategoryFilterMenu";

import {
  EXPLORE_SUBS,
  ACCOMMODATION_SUBS,
  TRANSPORT_SUBS,
} from "./bucketConstants";
// Register the English locale
countries.registerLocale(eng);

const ItineraryDetails = ({
  item,
  activities,
  hotels,
  flights,
  RVs,
  onActivityClick,
  onUpdateClick,
  onDayChange,
  selectedDayIndex,
  handleHotelUpdateClick,
  handleRVsUpdateClick,
  handleFlightUpdateClick,
  handleAllActivitiesUpdateClick,
  lastUpdatedDate,
  tripDetails,
  setTripDetails,
  tripId,
  isBaseScreen,
  exploreSelections,
  setExploreSelections,
  accommodationSelections,
  setAccommodationSelections,
  transportSelections,
  setTransportSelections,
  handleSuggestionClick,
  setIsAddActivitiesOpen,
  setIsAddAccommodationOpen,
  setIsAddFlightOpen,
  setIsAddRVOpen,
}) => {
  // console.log("RVs",RVs);
  // console.log("itinerary", item);
  const tabListRef = useRef(null);
  const [itinerary, setItinerary] = useState([]);
  const toast = useToast();
  const { convertCurrency, currency } = useCurrency(); // Access convertCurrency and currency from context
  const [totalCost, setTotalCost] = useState(0);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  /* ---------- NEW state + ref ---------- */
  const listRef = useRef(null);
  const [showScrollUp, setShowScrollUp] = useState(false);

  /* Show button only after scrolling ~250 px */
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const onScroll = () => setShowScrollUp(el.scrollTop > 250);
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () =>
    listRef.current?.scrollTo({ top: 0, behavior: "smooth" });

  const tabListscrollLeft = () => {
    if (tabListRef.current) {
      tabListRef.current.scrollBy({
        left: -300, // Adjust this value to control the scroll distance
        behavior: "smooth", // Smooth scrolling
      });
    }
  };

  const tabListscrollRight = () => {
    if (tabListRef.current) {
      tabListRef.current.scrollBy({
        left: 300, // Adjust this value to control the scroll distance
        behavior: "smooth", // Smooth scrolling
      });
    }
  };

  const checkScrollButtons = () => {
    if (tabListRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabListRef.current;
      setShowLeftArrow(scrollLeft > 1);
      setShowRightArrow(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 1);
    }
  };

  useEffect(() => {
    // Initial checks with multiple retries
    const initialCheck = () => {
      checkScrollButtons();
      // Retry after short delays to ensure content is fully loaded
      setTimeout(checkScrollButtons, 100);
      setTimeout(checkScrollButtons, 500);
      setTimeout(checkScrollButtons, 1000);
    };

    // Run initial check
    initialCheck();

    // Add event listeners
    const tabListElement = tabListRef.current;
    const handleResize = () => {
      requestAnimationFrame(checkScrollButtons);
    };

    tabListElement?.addEventListener("scroll", checkScrollButtons);
    window.addEventListener("resize", handleResize);
    window.addEventListener("load", initialCheck);

    // Observe changes in the tabList content
    const observer = new MutationObserver(checkScrollButtons);
    if (tabListElement) {
      observer.observe(tabListElement, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }

    // Cleanup
    return () => {
      tabListElement?.removeEventListener("scroll", checkScrollButtons);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("load", initialCheck);
      observer.disconnect();
    };
  }, [itinerary, selectedDayIndex]); // Add selectedDayIndex as dependency

  // Ensure tripDetails is not null and has a country property before accessing it
  const countryName = tripDetails?.country || "";
  const countryCode = countryName
    ? countries.getAlpha2Code(countryName, "en")
    : null;

  useEffect(() => {
    if (item && item.itinerary) {
      setItinerary(item.itinerary);
    }
  }, [item]);

  useEffect(() => {
    const fetchExpenses = async () => {
      try {
        const response = await axiosInstance.get(
          `/budget_planner/${tripId}/expenses`
        );
        const expensesData = response.data;

        if (Array.isArray(expensesData)) {
          // Calculate the total cost across all categories
          const total = expensesData.reduce((acc, category) => {
            if (category.expenses && Array.isArray(category.expenses)) {
              const categoryTotal = category.expenses.reduce(
                (catAcc, expense) => {
                  return catAcc + (expense.cost || 0); // Default to 0 if cost is undefined
                },
                0
              );
              return acc + categoryTotal;
            } else {
              console.warn("Expenses is not an array for category:", category);
            }
            return acc;
          }, 0);

          setTotalCost(total); // Set the total cost in the state
        } else {
          console.error("Response data is not an array");
        }
      } catch (error) {
        console.error("Error fetching expenses:", error);
      }
    };

    if (tripId) {
      fetchExpenses();
    }
  }, [tripId]);

  // Convert the total cost to the selected currency
  const convertedTotalCost = parseFloat(convertCurrency(totalCost, "USD")) || 0;

  if (!item) {
    return null; // Guard against null items
  }

  const { mainImage, travelDates, title, lastUpdated, description } =
    item || {};

  const handleUpdateClickInternal = () => {
    onUpdateClick(); // Trigger the parent's update function
  };

  const handleHotelUpdateClickInternal = () => {
    handleHotelUpdateClick(); // Trigger the parent's update function
  };

  const handleRVsUpdateClickInternal = () => {
    handleRVsUpdateClick(); // Trigger the parent's update function
  };

  const handleFlightUpdateClickInternal = () => {
    handleFlightUpdateClick(); // Trigger the parent's update function
  };

  const handleActivitiesUpdateClickInternal = () => {
    handleAllActivitiesUpdateClick(); // Trigger the parent's update function
  };

  const countryFlagLink = (countryName) => {
    // console.log("countryName", countryName);
    const countryCode = countryName
      ? countries.getAlpha2Code(countryName, "en")
      : null;
    if (!countryCode) {
      return "../assets/images/banner.jpg";
    }

    return `https://flagcdn.com/w320/${countryCode.toLowerCase()}.png`;
  };

  const firstDayImage =
    itinerary[0]?.activity_detail_list?.[0]?.thumbnailHiResURL;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const suffix =
      day === 1 || day === 21 || day === 31
        ? "st"
        : day === 2 || day === 22
          ? "nd"
          : day === 3 || day === 23
            ? "rd"
            : "th";

    return date
      .toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
      .replace(day.toString(), `${day}${suffix}`);
  };

  // const translatedTitle = Translate({
  //   text: item.trip_name || "Ongoing Trip",
  //   as: "string",
  // });

  return (
    <>
      <Helmet>
        <title>{item.trip_name}</title>
      </Helmet>

      <Box
        ref={listRef}
        right="0"
        top="0"
        h={{ base: "80vh", md: "92vh" }}
        maxW="99vw"
        p={{ base: 2, md: 5 }}
        pt={{ base: 2, md: 2 }}
        bg="white"
        zIndex="modal"
        overflowY={{ base: "auto", md: "auto" }}
      >
        <VStack spacing={{ base: 4, md: 2 }} align="stretch" pt="2">
          <Image
            src={firstDayImage}
            fallbackSrc={countryFlagLink(itinerary?.[0]?.country)}
            alt={itinerary[0]?.location || "No Description Available"}
            borderRadius="md"
            boxSize="100%"
            objectFit="cover"
            mb={0}
            maxH={{ base: "200px", md: "300px" }}
          />
          <VStack
            spacing={4}
            alignItems="flex-start"
            justifyContent="flex-start"
            mt={{ base: 2, md: 5 }}
          >
            <Text fontSize={{ base: "xs", md: "sm" }} mb={0}>
              <Translate
                text={`${tripDetails?.departure_date
                    ? formatDate(tripDetails.departure_date)
                    : "N/A"
                  } to ${tripDetails?.return_date
                    ? formatDate(tripDetails.return_date)
                    : "N/A"
                  }`}
              />
            </Text>

            <Heading size={{ base: "sm", md: "md" }} mb={0}>
              <Translate text={item.trip_name || "No Title Available"} />
              {countryCode ? (
                <CountryFlag
                  countryCode={countryCode}
                  svg
                  style={{
                    width: "1.25em",
                    height: "1.25em",
                    marginLeft: "0.5em",
                    boxShadow:
                      "-4px 0 4px -2px rgba(0,0,0,0.1), 4px 0 4px -2px rgba(0,0,0,0.1)", // Shadows on left and right
                    backgroundColor: "#f0f0f0", // Light gray background
                    borderRadius: "4px", // Optional: rounded corners
                  }}
                />
              ) : (
                <p></p>
              )}
            </Heading>
            {/* Display converted total cost */}
            <Link
              as={RouterLink}
              to="/ai-travel-planner/budget-planner"
              fontSize={{ base: "xs", md: "sm" }}
              mb={0}
            >
              {currency}{" "}
              {convertedTotalCost ? Math.round(convertedTotalCost) : "N/A"}
            </Link>

            <Text size={{ base: "sm", md: "md" }} mb={0}>
              <Translate text={description || "No Description Available"} />
            </Text>
            <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500" mb={0}>
              <Translate
                text={`Last updated: ${lastUpdatedDate
                    ? formatDate(lastUpdatedDate.split(" ")[0])
                    : "N/A"
                  }`}
              />
            </Text>
          </VStack>
          <InfoCard
            tripId={tripId}
            tripDetails={tripDetails}
            setTripDetails={setTripDetails}
            city={itinerary[selectedDayIndex]?.location}
            currency={currency}
          />
          <Text
            fontSize={{ base: "lg", md: "xl" }}
            fontWeight="bold"
            mb={0}
            mt={0}
            p={2}
            pl={0}
          >
            <Translate text="Itinerary" />
          </Text>
          <Tabs
            variant="unstyled"
            index={selectedDayIndex}
            onChange={(index) => onDayChange(index)}
          >
            {/* Day tabs with scroll buttons */}
            <Box position="relative" w="100%">
              {showLeftArrow && (
                <IconButton
                  aria-label="Scroll Left"
                  icon={<ChevronLeftIcon />}
                  onClick={tabListscrollLeft}
                  isRound={true}
                  size="xs"
                  position="absolute"
                  top="40%"
                  left="0px"
                  transform="translateY(-50%)"
                  backgroundColor="white"
                  boxShadow="base"
                  zIndex="overlay"
                />
              )}
              <TabList
                ref={tabListRef}
                overflowX="auto"
                w="100%"
                whiteSpace="nowrap"
                sx={{
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                }}
                pb={2}
              >
                {itinerary.map((day, idx) => (
                  <Tab
                    key={idx}
                    _selected={{ bg: "white", border: "2px solid black" }}
                    p={{ base: 2, md: 2 }}
                    mr={{ base: 3, md: 2, lg: 4 }}
                    border="2px solid"
                    borderColor="gray.200"
                    borderRadius="xl"
                    flexShrink={0}
                  >
                    <VStack alignItems="flex-start" justifyContent="flex-start">
                      <Heading
                        fontSize={{ base: "sm", md: "md" }}
                        fontWeight="bold"
                        mb={0}
                        fontFamily="Arial"
                      >
                        <Translate text="Day" /> {idx + 1}
                      </Heading>
                      <Text
                        fontSize={{ base: "xs", md: "xs" }}
                        color="gray.500"
                        mb={0}
                        fontFamily="Arial"
                      >
                        <Translate text={day.location} />
                      </Text>
                    </VStack>
                  </Tab>
                ))}
              </TabList>
              {showRightArrow && (
                <IconButton
                  aria-label="Scroll Right"
                  icon={<ChevronRightIcon />}
                  onClick={tabListscrollRight}
                  isRound={true}
                  size="xs"
                  position="absolute"
                  top="40%"
                  right="0px"
                  transform="translateY(-50%)"
                  backgroundColor="white"
                  boxShadow="base"
                  zIndex="overlay"
                />
              )}
            </Box>

            {/* Panels for each day */}
            <TabPanels>
              {itinerary.map((day, idx) => {
                {
                  /* const filteredActivities =
                  exploreSelections.length > 0
                    ? day.activity_detail_list.filter((a) =>
                        exploreSelections.includes(a.type)
                      )
                    : day.activity_detail_list; */
                }
                const filteredActivities = (() => {
                  // If "Other" is selected, show items whose type is _not_ in any of the other prefs:
                  if (exploreSelections.includes("other")) {
                    const otherPrefs = EXPLORE_SUBS.filter(
                      (s) => s.pref !== "other"
                    ).map((s) => s.pref);
                    return day.activity_detail_list.filter(
                      (a) => !otherPrefs.includes(a.type)
                    );
                  }
                  // Otherwise if any prefs selected, only show those types
                  if (exploreSelections.length > 0) {
                    return day.activity_detail_list.filter((a) =>
                      exploreSelections.includes(a.type)
                    );
                  }
                  // No filter → show all
                  return day.activity_detail_list;
                })();
                return (
                  <TabPanel
                    key={idx}
                    p={{ base: 3, md: 5 }}
                    border="2px solid"
                    borderColor="gray.200"
                    borderRadius="xl"
                  >
                    {/* Day header */}
                    <Heading
                      fontSize={{ base: "20px", md: "24px" }}
                      fontFamily="Arial"
                      mb={0}
                    >
                      <Translate text={day.current_day_name} />
                    </Heading>

                    {/* Time-of-day plan */}
                    <VStack
                      mt={5}
                      spacing={4}
                      alignItems="flex-start"
                      justifyContent="flex-start"
                    >
                      {Object.entries(day.plan).map(([timeOfDay, text], i) => (
                        <VStack
                          key={i}
                          spacing={2}
                          alignItems="flex-start"
                          justifyContent="flex-start"
                        >
                          <Heading
                            size={{ base: "sm", md: "xs" }}
                            fontFamily="Arial"
                            mb={0}
                          >
                            <Translate
                              text={
                                timeOfDay.charAt(0).toUpperCase() +
                                timeOfDay.slice(1)
                              }
                            />
                          </Heading>
                          <Text fontFamily="Arial" mb={0}>
                            <Translate text={text} />
                          </Text>
                        </VStack>
                      ))}
                    </VStack>

                    {/* Explore section with filter */}
                    <VStack mt={5}>
                      <HStack spacing={0} w="full">
                        <Heading
                          mb={0}
                          fontSize={{ base: "sm", md: "md", lg: "lg" }}
                          textAlign="left"
                        >
                          <Translate text="Activities" />
                        </Heading>
                        <CategoryFilterMenu
                          tag="explore"
                          label="Explore Filter"
                          isActive={exploreSelections.length > 0}
                          options={EXPLORE_SUBS}
                          selections={exploreSelections}
                          toggleSelection={(pref) =>
                            setExploreSelections((prev) =>
                              prev.includes(pref)
                                ? prev.filter((p) => p !== pref)
                                : [...prev, pref]
                            )
                          }
                          useIcon={true}
                        />

                        <Spacer />
                        <Menu>
                          <MenuButton
                            as={Button}
                            aria-label="Update activities"
                            bg="#1A202C"
                            color="white"
                            _hover="none"
                            _active="none"
                            size={{ base: "xs", md: "xs", lg: "sm" }}
                            fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                            rightIcon={<ChevronDownIcon />}
                            onClick={handleUpdateClickInternal}
                          >
                            <Translate text="Update Activities" />
                          </MenuButton>
                          <MenuList>
                            <MenuItem
                              onClick={() => setIsAddActivitiesOpen(true)}
                              fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                            >
                              <Translate text="Add Bucket List Items" />
                            </MenuItem>
                            <MenuItem
                              onClick={() =>
                                handleSuggestionClick(
                                  "Could you give me more recommendation for activities?",
                                  "explore_conversation"
                                )
                              }
                              fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                            >
                              <Translate text="Get more Recommendations" />
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </HStack>
                      {filteredActivities.length === 0 ? (
                        <Text fontSize="sm" color="gray.500" mt={3}>
                          <Translate text="No activities found for the selected category." />
                        </Text>
                      ) : (
                        <Activities
                          data={filteredActivities}
                          onActivityClick={onActivityClick}
                          itinerary="true"
                        />
                      )}
                    </VStack>
                  </TabPanel>
                );
              })}
            </TabPanels>
          </Tabs>
        </VStack>

        {hotels?.length > 0 && (
          <>
            <HStack
              spacing={0}
              align="center"
              justifyContent="space-between"
              mt={6}
              mb={0}
              flexDirection={{ base: "row", md: "row" }}
            >
              <Heading fontSize={{ base: "sm", md: "md", lg: "xl" }} mb={0}>
                <Translate text="Accommodations" />
              </Heading>

              <CategoryFilterMenu
                tag="accommodation"
                label="Accommodations"
                isActive={accommodationSelections.length > 0}
                options={ACCOMMODATION_SUBS}
                selections={accommodationSelections}
                toggleSelection={(pref) => {
                  setAccommodationSelections((prev) => {
                    // If they clicked "Other":
                    if (pref === "other") {
                      // if it was already selected, un‐select it; otherwise select only "other"
                      return prev.includes("other") ? [] : ["other"];
                    }
                    // They clicked a normal sub‐category:
                    // 1) remove any "other" if present
                    const withoutOther = prev.filter((p) => p !== "other");
                    // 2) toggle the clicked pref
                    if (withoutOther.includes(pref)) {
                      // un‐select it
                      return withoutOther.filter((p) => p !== pref);
                    } else {
                      // select it
                      return [...withoutOther, pref];
                    }
                  });
                }}
                useIcon={true}
              />

              <Spacer />
              <Menu>
                <MenuButton
                  as={Button}
                  aria-label="Update hotels"
                  bg="#1A202C"
                  color="white"
                  _hover="none"
                  _active="none"
                  size={{ base: "xs", md: "xs", lg: "sm" }}
                  fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                  rightIcon={<ChevronDownIcon />}
                  onClick={handleHotelUpdateClickInternal}
                >
                  <Translate text="Update Accommodations" />
                </MenuButton>
                <MenuList>
                  <MenuItem
                    onClick={() => setIsAddAccommodationOpen(true)}
                    fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                  >
                    <Translate text="Add Bucket List Items" />
                  </MenuItem>
                  <MenuItem
                    onClick={() =>
                      handleSuggestionClick(
                        "Could you give me more recommendation for accomodations?",
                        "accommodation_conversation"
                      )
                    }
                    fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                  >
                    <Translate text="Get more Recommendations" />
                  </MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          </>
        )}

        {(() => {
          // Build the filtered list with "other" logic
          let filtered;
          if (accommodationSelections.includes("other")) {
            // Show hotels whose type isn't in any other pref
            const otherPrefs = ACCOMMODATION_SUBS.filter(
              (s) => s.pref !== "other"
            ).map((s) => s.pref);
            filtered = hotels.filter(
              (h) => !otherPrefs.includes(h.type?.toLowerCase())
            );
          } else if (accommodationSelections.length > 0) {
            // Normal multi-select
            filtered = hotels.filter((h) =>
              accommodationSelections.includes(h.type?.toLowerCase())
            );
          } else {
            // No filter
            filtered = hotels;
          }

          return filtered.length === 0 ? (
            <Text fontSize="sm" color="gray.500" mt={3}>
              <Translate text="No accommodations found for the selected category." />
            </Text>
          ) : (
            <HotelsCard hotels={filtered} onActivityClick={onActivityClick} itinerary="true"/>
          );
        })()}
        <HStack spacing={0} align="center" mt={6}>
          <Heading
            spacing={0}
            fontSize={{ base: "sm", md: "md", lg: "xl" }}
            mb={0}
          >
            <Translate text="Transportation" />
          </Heading>
          <CategoryFilterMenu
            tag="transportation"
            label="Filter Transportation"
            isActive={transportSelections.length > 0}
            options={TRANSPORT_SUBS}
            selections={transportSelections}
            toggleSelection={(pref) =>
              setTransportSelections((prev) =>
                prev.includes(pref)
                  ? prev.filter((p) => p !== pref)
                  : [...prev, pref]
              )
            }
            useIcon={true}
          />
        </HStack>

        {/* Flights Section */}
        {(transportSelections.length === 0 ||
          transportSelections.includes("flight")) &&
          flights?.length > 0 && (
            <>
              <HStack
                align="center"
                justifyContent="space-between"
                mt={4}
                mb={0}
                mr={{ base: 1, md: 0, lg: 0 }}
                flexDirection={{ base: "row", md: "row" }}
              >
                <Heading
                  mb={0}
                  fontSize={{ base: "sm", md: "md", lg: "xl" }}
                  color="gray.500"
                >
                  <Translate text="Flights" />
                </Heading>
                <Menu>
                  <MenuButton
                    as={Button}
                    aria-label="Update flight"
                    bg="#1A202C"
                    color="white"
                    _hover="none"
                    _active="none"
                    size={{ base: "xs", md: "xs", lg: "sm" }}
                    fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    rightIcon={<ChevronDownIcon />}
                    onClick={handleFlightUpdateClickInternal}
                  >
                    <Translate text="Update Flights" />
                  </MenuButton>
                  <MenuList>
                    <MenuItem
                      onClick={() => setIsAddFlightOpen(true)}
                      fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    >
                      <Translate text="Add Bucket List Items" />
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        handleSuggestionClick(
                          "Could you give me more recommendation for flights?",
                          "transportation_conversation"
                        )
                      }
                      fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    >
                      <Translate text="Get more Recommendations" />
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
              <FlightCard data={flights} onActivityClick={onActivityClick} itinerary="true"/>
            </>
          )}

        {/* RV Rentals Section */}
        {(transportSelections.length === 0 ||
          transportSelections.includes("rv")) &&
          RVs?.length > 0 && (
            <>
              <HStack
                align="center"
                justifyContent="space-between"
                mt={4}
                mb={0}
                mr={{ base: 1, md: 0, lg: 0 }}
                flexDirection={{ base: "row", md: "row" }}
              >

                <Heading
                  mb={0}
                  fontSize={{ base: "sm", md: "md", lg: "xl" }}
                  color="gray.500"
                >
                  <Translate text="RVs for Rental" />
                </Heading>
                <Menu>
                  <MenuButton
                    as={Button}
                    aria-label="Update RVs"
                    bg="#1A202C"
                    color="white"
                    _hover="none"
                    _active="none"
                    size={{ base: "xs", md: "xs", lg: "sm" }}
                    fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    rightIcon={<ChevronDownIcon />}
                    onClick={handleRVsUpdateClickInternal}
                  >
                    <Translate text="Update RVs" />
                  </MenuButton>
                  <MenuList>
                    <MenuItem
                      onClick={() => setIsAddRVOpen(true)}
                      fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    >
                      <Translate text="Add Bucket List Items" />
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        handleSuggestionClick(
                          "Could you give me more recommendation for RVs?"
                        )
                      }
                      fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    >
                      <Translate text="Get more Recommendations" />
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
              <RVRentalCard RVs={RVs} onActivityClick={onActivityClick} itinerary="true"/>
            </>
          )}

        <HStack
          align="left"
          justifyContent="space-between"
          mt={4}
          mb={0}
          flexDirection={{ base: "column", md: "column" }}
        >
          <Text fontSize={{ base: "sm", md: "sm", lg: "sm", xl: "md" }} mb={0}>
            <Translate text="Make a difference with your contribution. Offset your CO2 emissions today and help build a more sustainable future." />
          </Text>
          <Button
            leftIcon={<DonateWhiteIcon width="24px" height="24px" />}
            as={RouterLink}
            to="/donations"
            bg="#286328"
            color="white"
            size="sm"
            _hover="none"
            width={{ base: "50%", md: "30%" }}
          >
            <Translate text="Contribute" />
          </Button>
        </HStack>
        {/* ② Scroll‑up button */}
        {showScrollUp && (
          <IconButton
            icon={<ArrowUpIcon boxSize={5} />}
            position="fixed"
            bottom={{ base: 16, md: 6 }}
            right={{ base: 4, md: 8 }}
            size="md"
            bg="#1A202C"
            color="white"
            _hover={{ bg: "#1A202C" }}
            borderRadius="full"
            zIndex="1200"
            onClick={scrollToTop}
          />
        )}
      </Box>
    </>
  );
};

export default ItineraryDetails;
